/**
 * View-model builders для UI отчёта сверки (фича 023, этап 7b).
 *
 * Чистые функции, преобразующие server-side контракты `@efes/shared`
 * (фича 022) в frontend view-models, удобные для рендеринга.
 *
 * Никаких новых server-side моделей — только производные представления.
 * Архитектура: `data-model.md` E1, E2.
 */

import type { Discrepancy, RunReport, RunReportLimitExceeded } from "@efes/shared";

/** Сводные счётчики по severity (для баннера сводки FR-017). */
export interface SeverityCounters {
	error: number;
	warning: number;
	total: number;
}

/**
 * View-model одной карточки Discrepancy (data-model.md E1).
 *
 * Расширяет `Discrepancy` computed-полями, нужными UI:
 * - `severity` пересчитывается из `violations[]` (defensive — на случай
 *   рассинхрона backend-вычисления; FR-009a).
 * - `hasRelated` — управляет рендерингом секции «Связанные расхождения»
 *   (FR-013b: пустые блоки запрещены).
 */
export interface DiscrepancyCardViewModel extends Discrepancy {
	/** Максимальное severity среди violations: error > warning. */
	severity: "error" | "warning";
	/** True, если есть связанные Discrepancy для drill-down (FR-013). */
	hasRelated: boolean;
}

/**
 * Discriminated union состояний страницы отчёта (data-model.md E2).
 *
 * - `loading` — TanStack Query ещё не получил данные.
 * - `ok` — отчёт получен, рендерим список карточек.
 * - `limit-exceeded` — backend вернул LIMIT_EXCEEDED (FR-023).
 * - `error` — после исчерпания TanStack Query retry (FR-028c).
 * - `not-found` — 404 от GET /runs/:runId (run удалён или не существует).
 */
export type RunReportViewModel =
	| { status: "loading" }
	| {
			status: "ok";
			report: RunReport;
			cards: DiscrepancyCardViewModel[];
			counters: SeverityCounters;
	  }
	| {
			status: "limit-exceeded";
			runId: string;
			setName: string;
			totalCount: number;
			severityBreakdown: { error: number; warning: number };
	  }
	| { status: "error"; canRetry: boolean }
	| { status: "not-found" };

/**
 * Преобразует `Discrepancy` в `DiscrepancyCardViewModel` (data-model.md E1).
 *
 * Severity вычисляется из `violations[]`: если есть хотя бы одно error → error,
 * иначе warning. Если массив пуст (теоретически невозможно — backend всегда
 * включает минимум одно violation) → fallback на "warning".
 *
 * Sprint 6 / C1.3: vm-поле `hasNumericComparisons` удалено (мёртвый код —
 * `pickCardVariant` его не читал). Логика выбора matrix vs aggregated
 * полностью в `pickCardVariant.ts`.
 */
export function toDiscrepancyCardViewModel(d: Discrepancy): DiscrepancyCardViewModel {
	const severity: "error" | "warning" = d.violations.some((v) => v.severity === "error")
		? "error"
		: "warning";
	return {
		...d,
		severity,
		hasRelated: d.relatedDiscrepancyIds.length > 0,
	};
}

/**
 * Подсчитывает counters по severity среди карточек (FR-017, FR-018).
 *
 * Принимает любой массив объектов с полем `severity` — работает как для
 * сырых `Discrepancy`, так и для уже преобразованных `DiscrepancyCardViewModel`.
 */
export function countSeverity(
	items: ReadonlyArray<{ severity: "error" | "warning" }>,
): SeverityCounters {
	let error = 0;
	let warning = 0;
	for (const item of items) {
		if (item.severity === "error") error += 1;
		else warning += 1;
	}
	return { error, warning, total: error + warning };
}

/**
 * Преобразует ответ TanStack Query (`data` + `error`) в `RunReportViewModel`.
 *
 * - `error.name === "RunNotFoundError"` → `not-found` (404).
 * - Любая другая ошибка → `error` с `canRetry: true` (FR-028c).
 * - Пустой `report` → `loading`.
 * - `report.status === "LIMIT_EXCEEDED"` → `limit-exceeded` (FR-023).
 * - Иначе — `ok` с предвычисленными `cards` и `counters`.
 */
export function toRunReportViewModel(
	report: RunReport | RunReportLimitExceeded | null | undefined,
	error?: Error | null,
): RunReportViewModel {
	if (error) {
		if (error.name === "RunNotFoundError") {
			return { status: "not-found" };
		}
		return { status: "error", canRetry: true };
	}
	if (!report) {
		return { status: "loading" };
	}
	if ("status" in report && report.status === "LIMIT_EXCEEDED") {
		return {
			status: "limit-exceeded",
			runId: report.run.id,
			setName: report.run.parametersSnapshot.set.name,
			totalCount: report.totalCount,
			severityBreakdown: report.severityBreakdown,
		};
	}
	const okReport = report as RunReport;
	const cards = okReport.discrepancies.map(toDiscrepancyCardViewModel);
	return {
		status: "ok",
		report: okReport,
		cards,
		counters: countSeverity(cards),
	};
}
